/**
 * 배치 처리
 *
 * 큐의 이벤트를 일정 크기/주기로 묶어서 전송합니다.
 */

import { EventQueue } from './queue';
import { EventSender } from './sender';

export class BatchProcessor {
  private queue: EventQueue;
  private sender: EventSender;
  private batchSize: number;
  private flushInterval: number;
  private timer: ReturnType<typeof setInterval> | null = null;
  private flushing = false;
  private debug: boolean;

  constructor(
    queue: EventQueue,
    sender: EventSender,
    batchSize: number = 10,
    flushInterval: number = 5000,
    debug: boolean = false,
  ) {
    this.queue = queue;
    this.sender = sender;
    this.batchSize = batchSize;
    this.flushInterval = flushInterval;
    this.debug = debug;
  }

  start(): void {
    if (this.timer) return;

    this.timer = setInterval(() => {
      void this.flush();
    }, this.flushInterval);

    this.bindPageLifecycleEvents();
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    this.unbindPageLifecycleEvents();
  }

  async checkAndFlush(): Promise<void> {
    if (this.queue.size() >= this.batchSize) {
      await this.flush();
    }
  }

  async flush(): Promise<void> {
    if (this.flushing || this.queue.isEmpty()) return;

    this.flushing = true;

    try {
      while (!this.queue.isEmpty()) {
        const events = this.queue.dequeue(this.batchSize);
        const result = await this.sender.send(events);

        if (!result.success && result.retryable) {
          this.queue.restore(events);
          break;
        }
      }
    } finally {
      this.flushing = false;
    }
  }

  flushSync(): void {
    if (this.queue.isEmpty()) return;

    const events = this.queue.flush();
    this.sender.sendSync(events);
  }

  private onVisibilityChange = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this.flushSync();
    }
  };

  private onBeforeUnload = (): void => {
    this.flushSync();
  };

  private bindPageLifecycleEvents(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.onBeforeUnload);
    }
  }

  private unbindPageLifecycleEvents(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.onBeforeUnload);
    }
  }
}
